import { Outcome } from "@ethossoftworks/outcome"
import { Bloc } from "@lib/bloc/Bloc"
import { arrayToObject } from "@lib/TypeUtil"
import { AsyncStatus } from "@model/AsyncStatus"
import { ServiceItemForm, serviceItemsTotals } from "@model/serviceRequests/ServiceItemForm"
import { WorkOrder } from "@model/workOrders/WorkOrder"
import { WorkOrderService } from "@service/workOrders/WorkOrderService"
import { workRequestToServiceItemForm } from "@service/workRequests/MainWorkRequestService"
import { WorkRequestService } from "@service/workRequests/WorkRequestService"

/**
 * Action Item #6766: Work History Overlay.
 *
 * A single closed work order of the asset plus the service items that belong to it. The
 * service items are loaded up-front (in one request for every listed work order) because the
 * search box has to match on service item fields, not only on work order fields.
 */
export type WorkHistoryEntry = {
    workOrder: WorkOrder
    serviceItems: ServiceItemForm[]
    serviceItemCount: number
    totalCost: number
}

export type WorkHistoryOverlayState = {
    isVisible: boolean
    assetId: number | null
    effectStatus: Record<WorkHistoryOverlayEffect, AsyncStatus>
    entries: WorkHistoryEntry[]
    searchText: string
    selectedWorkOrderId: number | null
}

export enum WorkHistoryOverlayEffect {
    Fetch = "fetch",
    FetchDetails = "fetchDetails",
}

export class WorkHistoryOverlayBloc extends Bloc<WorkHistoryOverlayState> {
    constructor(private workOrderService: WorkOrderService, private workRequestService: WorkRequestService) {
        super(newWorkHistoryOverlayState(), { persistStateOnDispose: false })
    }

    show = (assetId: number) => {
        this.update({
            isVisible: true,
            assetId: assetId,
            searchText: "",
            selectedWorkOrderId: null,
            entries: [],
        })

        this.fetchHistory(assetId)
    }

    /**
     * Only flips visibility so the content stays intact while the modal fades out. The rest of
     * the state is reset by show() the next time the overlay is opened.
     */
    hide = () => this.update({ isVisible: false })

    searchTextChanged = (searchText: string) => this.update({ searchText: searchText })

    workOrderSelected = (workOrderId: number | null) => {
        this.update({ selectedWorkOrderId: workOrderId })
        if (workOrderId != null) this.fetchWorkOrderDetails(workOrderId)
    }

    /**
     * The bulk GetWorkOrders request returns createdByUserData as null (createdByID alone is a
     * user id, which can't be resolved against contacts - those are keyed by person id). The
     * single work order request does include createdByUserData, so the selected entry is
     * enriched with it on demand.
     */
    private fetchWorkOrderDetails = (workOrderId: number) => {
        const entry = this.state.entries.find((it) => it.workOrder.id === workOrderId)
        if (!entry || entry.workOrder.createdByUserData != null || !entry.workOrder.createdByID) return

        this.effect({
            id: `${WorkHistoryOverlayEffect.FetchDetails}-${workOrderId}`,
            block: async (job) => {
                const outcome = await job.pause(this.workOrderService.fetchWorkOrder(workOrderId))
                // On error the entry keeps showing the bulk data - nothing else to surface here
                if (outcome.isError()) return outcome

                this.update({
                    entries: this.state.entries.map((it) =>
                        it.workOrder.id === workOrderId ? { ...it, workOrder: outcome.value } : it
                    ),
                })
                return Outcome.ok(undefined)
            },
        })
    }

    fetchHistory = (assetId: number) =>
        this.effect({
            id: WorkHistoryOverlayEffect.Fetch,
            block: async (job) => {
                this.effectStatusChanged(WorkHistoryOverlayEffect.Fetch, AsyncStatus.busy())

                const workOrdersOutcome = await job.pause(this.workOrderService.fetchClosedWorkOrdersForAsset(assetId))
                if (workOrdersOutcome.isError()) {
                    this.effectStatusChanged(WorkHistoryOverlayEffect.Fetch, AsyncStatus.error(undefined))
                    return Outcome.error(undefined)
                }

                const workOrders = workOrdersOutcome.value
                if (workOrders.length === 0) {
                    this.update({ entries: [] })
                    this.effectStatusChanged(WorkHistoryOverlayEffect.Fetch, AsyncStatus.idle())
                    return Outcome.ok(undefined)
                }

                const workRequestsOutcome = await job.pause(
                    this.workRequestService.fetchWorkRequestsForWorkOrders(workOrders.map((it) => it.id))
                )
                if (workRequestsOutcome.isError()) {
                    this.effectStatusChanged(WorkHistoryOverlayEffect.Fetch, AsyncStatus.error(undefined))
                    return Outcome.error(undefined)
                }

                const serviceItemsByWorkOrder = new Map<number, ServiceItemForm[]>()
                workRequestsOutcome.value.forEach((workRequest, idx) => {
                    if (workRequest.workOrderId == null) return
                    const items = serviceItemsByWorkOrder.get(workRequest.workOrderId) ?? []
                    items.push(workRequestToServiceItemForm(workRequest, idx))
                    serviceItemsByWorkOrder.set(workRequest.workOrderId, items)
                })

                const entries = workOrders
                    .map((workOrder): WorkHistoryEntry => {
                        const serviceItems = serviceItemsByWorkOrder.get(workOrder.id) ?? []
                        return {
                            workOrder: workOrder,
                            serviceItems: serviceItems,
                            serviceItemCount: serviceItems.length,
                            totalCost: serviceItemsTotals(serviceItems).total,
                        }
                    })
                    .sort(compareByClosedDateDesc)

                // The most recently closed work order is selected automatically when the overlay opens
                this.update({ entries: entries })
                this.workOrderSelected(entries[0]?.workOrder.id ?? null)

                this.effectStatusChanged(WorkHistoryOverlayEffect.Fetch, AsyncStatus.idle())
                return Outcome.ok(undefined)
            },
        })

    private effectStatusChanged = (effect: WorkHistoryOverlayEffect, status: AsyncStatus) =>
        this.update({
            effectStatus: {
                ...this.state.effectStatus,
                [effect]: status,
            },
        })
}

/** Most recently closed work orders first. Entries without a closed date fall to the bottom. */
function compareByClosedDateDesc(a: WorkHistoryEntry, b: WorkHistoryEntry): number {
    const aTime = a.workOrder.closedDate?.getTime() ?? 0
    const bTime = b.workOrder.closedDate?.getTime() ?? 0
    return bTime - aTime
}

function newWorkHistoryOverlayState(): WorkHistoryOverlayState {
    return {
        isVisible: false,
        assetId: null,
        effectStatus: arrayToObject(Object.values(WorkHistoryOverlayEffect), (it) => [it, AsyncStatus.idle()]),
        entries: [],
        searchText: "",
        selectedWorkOrderId: null,
    }
}
